import { useEffect, useRef, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { ProductPicker } from "@/components/ProductPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  UploadCloud, FileSpreadsheet, ArrowRight, ArrowLeft, CheckCircle2,
  AlertTriangle, ImagePlus, ShieldAlert, Store,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { CATALOG_ISSUE_LABELS, CATALOG_CAMBIO_LABELS, CATALOG_MATCH_LABELS, CSV_AVISO_LABELS } from "@/lib/format";

const CAMPOS = [
  { key: "nombre", label: "Nombre", req: true },
  { key: "categoria", label: "Categoría" },
  { key: "marca", label: "Marca" },
  { key: "precio", label: "Precio" },
  { key: "costo", label: "Costo" },
  { key: "stock_actual", label: "Stock inicial" },
  { key: "sku", label: "SKU" },
  { key: "descripcion", label: "Descripción" },
];
// Solo para el archivo de la exportación de Shopify (trae el link de la foto principal y las etiquetas).
const CAMPOS_WEB = [
  { key: "foto_url", label: "Foto (link)" },
  { key: "etiquetas", label: "Etiquetas" },
];
const ORIGEN_WEB = ["shopify_api", "shopify_csv", "shopify_auto"];
const FILAS_POR_VEZ = 60;

// Un número escrito en un campo: vacío o no numérico = sin valor (nunca 0).
const numeroOnull = (v) => {
  if (v === null || v === undefined || String(v).trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// Campo numérico de una fila. Solo avisa un valor válido; si queda vacío, al salir vuelve al último valor guardado.
// Así una fila sin cantidad no pasa a «cantidad 0» por tocar otro dato, y vaciar el campo no borra la cantidad en silencio.
function NumeroInput({ valor, onGuardar, entero, ...props }) {
  const [texto, setTexto] = useState(valor === null || valor === undefined ? "" : String(valor));
  const ultimo = useRef(numeroOnull(valor));
  const valido = (n) => n !== null && (!entero || Number.isInteger(n));
  const cambiar = (e) => {
    setTexto(e.target.value);
    const n = numeroOnull(e.target.value);
    if (!valido(n)) return;
    ultimo.current = n;
    onGuardar(n);
  };
  const salir = () => {
    if (!valido(numeroOnull(texto))) setTexto(ultimo.current === null ? "" : String(ultimo.current));
  };
  return <Input type="number" value={texto} onChange={cambiar} onBlur={salir} {...props} />;
}

function Stepper({ step }) {
  const steps = ["Subir y mapear", "Revisar filas", "Confirmar"];
  return (
    <div className="flex items-center gap-2 mb-6">
      {steps.map((s, i) => (
        <div key={s} className="flex items-center gap-2 flex-1">
          <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-semibold shrink-0 ${i + 1 <= step ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
            {i + 1 < step ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
          </div>
          <span className={`text-sm hidden sm:inline ${i + 1 === step ? "font-semibold" : "text-muted-foreground"}`}>{s}</span>
          {i < steps.length - 1 && <div className="flex-1 h-px bg-border" />}
        </div>
      ))}
    </div>
  );
}

function BulkPhotos() {
  const [results, setResults] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const inputRef = useRef(null);

  const upload = async (files) => {
    if (!files?.length) return;
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post("/catalog-import/fotos-masivas", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResults((prev) => [...prev, ...data.map((d) => ({ ...d, target: d.product_id_sugerido || null }))]);
      toast.success(`${data.length} foto(s) lista(s) para asignar`);
    } catch { toast.error("No se pudieron subir las fotos"); }
  };

  const setTarget = (idx, pid) => setResults((prev) => prev.map((x, i) => i === idx ? { ...x, target: pid } : x));
  const quitar = (idx) => setResults((prev) => prev.filter((_, i) => i !== idx));

  const asignadas = results.filter((r) => r.target);

  const confirmarTodas = async () => {
    setAssigning(true);
    let ok = 0;
    for (const r of results) {
      if (!r.target) continue;
      try { await api.post(`/catalog-import/asignar-foto/${r.target}`, { path: r.path }); ok++; }
      catch { /* seguimos con las demás */ }
    }
    setResults((prev) => prev.filter((r) => !r.target));
    setAssigning(false);
    toast.success(`${ok} foto(s) asignada(s)`);
  };

  return (
    <div className="ms-card p-5 mt-6">
      <h2 className="font-head text-lg font-semibold mb-1">Carga masiva de fotos</h2>
      <p className="text-sm text-muted-foreground mb-3">Subí varias imágenes y asignalas a productos existentes.</p>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className={`rounded-2xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${dragOver ? "border-primary bg-accent/50" : "border-border"}`}
        data-testid="bulk-photos-dropzone"
      >
        <ImagePlus className="h-8 w-8 mx-auto mb-2 text-primary" strokeWidth={1.5} />
        <p className="text-sm text-muted-foreground">Arrastrá imágenes aquí o hacé clic para elegirlas</p>
        <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => upload(e.target.files)} data-testid="bulk-photos-input" />
      </div>

      {results.length > 0 && (
        <>
          <div className="mt-4 space-y-2">
            {results.map((r, idx) => (
              <div key={r.path} className="flex items-center gap-3 rounded-2xl border border-border p-3" data-testid={`bulk-photo-row-${idx}`}>
                <img src={fileUrl(r.path)} alt={r.filename} className="h-14 w-14 rounded-xl object-cover shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-1">
                    <p className="text-xs text-muted-foreground truncate flex-1">{r.filename}</p>
                    <Badge className={`text-[10px] rounded-full border-0 shrink-0 ${r.target ? "bg-emerald-50 text-emerald-600" : "bg-gray-100 text-gray-500"}`}>
                      {r.target ? "Asignada" : "Sin asignar"}
                    </Badge>
                  </div>
                  <ProductPicker value={r.target} onChange={(pid) => setTarget(idx, pid)} placeholder="Buscar producto…" testid={`bulk-photo-picker-${idx}`} />
                </div>
                <button onClick={() => quitar(idx)} className="text-xs text-red-500 shrink-0" data-testid={`bulk-photo-quitar-${idx}`}>Quitar de la lista</button>
              </div>
            ))}
          </div>
          <Button className="rounded-full bg-primary hover:bg-primary/90 mt-4 w-full" disabled={asignadas.length === 0 || assigning}
            onClick={confirmarTodas} data-testid="bulk-photos-confirmar">
            {assigning ? "Asignando…" : `Asignar ${asignadas.length} foto${asignadas.length === 1 ? "" : "s"}`}
          </Button>
        </>
      )}
    </div>
  );
}

function WipeDemoButton() {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  const doWipe = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/vaciar-demo", { confirmar: text });
      toast.success("Datos de demostración eliminados");
      setOpen(false); setText("");
      console.log(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };

  return (
    <>
      <Button variant="outline" className="rounded-full text-red-500 border-red-200 gap-2" onClick={() => setOpen(true)} data-testid="wipe-demo-button">
        <ShieldAlert className="h-4 w-4" /> Vaciar datos de demostración
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-red-500">Vaciar datos de demostración</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Esto elimina productos, pedidos, clientes, conversaciones, pagos y todo lo demo. NO toca usuarios ni configuración. Esta acción no se puede deshacer.
          </p>
          <div>
            <Label>Escribí <strong>BORRAR</strong> para confirmar</Label>
            <Input value={text} onChange={(e) => setText(e.target.value)} className="rounded-xl mt-1" data-testid="wipe-demo-confirm-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button disabled={text !== "BORRAR" || loading} onClick={doWipe} className="rounded-full bg-red-500 hover:bg-red-600" data-testid="wipe-demo-submit">
              {loading ? "Eliminando…" : "Sí, vaciar todo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function ImportCatalog() {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [imp, setImp] = useState(null);
  const [mapping, setMapping] = useState({});
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [filtro, setFiltro] = useState("todas");
  const [limite, setLimite] = useState(FILAS_POR_VEZ);
  const fileRef = useRef(null);
  const esWeb = ORIGEN_WEB.includes(imp?.origen);
  const esCsvWeb = imp?.origen === "shopify_csv";
  const sinPaso1 = imp?.origen === "shopify_api" || imp?.origen === "shopify_auto"; // la lista sale de la tienda: sin archivo ni columnas
  const cabeceras = imp?.headers || [];
  const vistaPrevia = imp?.preview || (imp?.raw_rows || []).slice(0, 10);
  const totalFilas = imp?.total_filas ?? (imp?.raw_rows || []).length;
  const resumenAvisos = (imp?.avisos || []).reduce((acc, a) => { acc[a.tipo] = (acc[a.tipo] || 0) + 1; return acc; }, {});

  // Desde la pantalla de Shopify se llega con ?importacion=ID: se abre esa lista para revisarla o ver cómo quedó.
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("importacion");
    if (!id) return undefined;
    let vivo = true;
    (async () => {
      try {
        const { data } = await api.get(`/catalog-import/${id}`);
        if (!vivo) return;
        if (data.status === "revision" || data.status === "confirmada") {
          setImp(data);
          setMapping(data.mapping || {});
          setStep(data.status === "confirmada" ? 3 : 2);
        } else {
          toast.error("Esa importación todavía no se mapeó: subí el archivo de nuevo.");
        }
      } catch (err) { if (vivo) toast.error(err.response?.data?.detail || "No se pudo abrir la importación"); }
    })();
    return () => { vivo = false; };
  }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/catalog-import/subir", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImp(data);
      if (data.mapping_sugerido) { // exportación de Shopify: las columnas ya vienen elegidas
        setMapping({ ...data.mapping_sugerido });
        toast.success(`Archivo de Shopify: ${data.total_filas} variantes detectadas`);
      } else {
        const auto = {};
        CAMPOS.forEach((c) => {
          const found = data.headers.find((h) => h.toLowerCase().includes(c.key.slice(0, 4)) || h.toLowerCase() === c.label.toLowerCase());
          if (found) auto[c.key] = found;
        });
        setMapping(auto);
        toast.success(`${data.total_filas} filas detectadas`);
      }
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo leer el archivo"); }
    finally { setUploading(false); }
  };

  const doMapear = async () => {
    if (!mapping.nombre) return toast.error("Mapeá al menos el campo Nombre");
    try {
      const { data } = await api.post(`/catalog-import/${imp.id}/mapear`, { mapping });
      setImp((prev) => ({ ...prev, ...data })); // conserva la vista previa y los avisos de la subida para poder volver al paso 1
      setStep(2);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al mapear"); }
  };

  const updateRow = async (row, patch) => {
    const updated = { ...row, ...patch };
    setImp((prev) => ({ ...prev, rows_procesadas: prev.rows_procesadas.map((r) => r.fila_index === row.fila_index ? updated : r) }));
    try {
      // Sin valor (null) = el servidor deja ese dato como está: una fila sin cantidad nunca pasa a «cantidad 0».
      await api.put(`/catalog-import/${imp.id}/fila/${row.fila_index}`, {
        accion: updated.accion, nombre: updated.nombre, categoria: updated.categoria, marca: updated.marca,
        precio: numeroOnull(updated.precio), costo: numeroOnull(updated.costo), stock_actual: numeroOnull(updated.stock_actual),
        sku: updated.sku, descripcion: updated.descripcion, product_id_match: updated.product_id_match,
      });
    } catch { toast.error("No se pudo guardar la fila"); }
  };

  const doConfirmar = async () => {
    setConfirming(true);
    try {
      const { data } = await api.post(`/catalog-import/${imp.id}/confirmar`);
      setImp(data);
      setStep(3);
      toast.success("Importación confirmada");
    } catch (err) { toast.error(err.response?.data?.detail || "Error al confirmar"); }
    finally { setConfirming(false); }
  };

  const reset = () => { setStep(1); setImp(null); setMapping({}); setFiltro("todas"); setLimite(FILAS_POR_VEZ); if (fileRef.current) fileRef.current.value = ""; };

  const counts = imp?.rows_procesadas?.reduce((acc, r) => { acc[r.accion] = (acc[r.accion] || 0) + 1; return acc; }, {}) || {};
  const FILTROS = [
    { key: "todas", texto: "filas", test: () => true },
    { key: "crear", texto: "a crear", test: (r) => r.accion === "crear" },
    { key: "actualizar", texto: "a actualizar", test: (r) => r.accion === "actualizar_existente" },
    { key: "omitir", texto: "a omitir", test: (r) => r.accion === "omitir" },
    { key: "avisos", texto: "con avisos", test: (r) => (r.issues || []).length > 0 || !!r.sugerido_id },
  ];
  const filas = (imp?.rows_procesadas || []).filter((FILTROS.find((f) => f.key === filtro) || FILTROS[0]).test);

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto">
      <PageHeader title="Importar catálogo" subtitle="Cargá tu catálogo real desde un CSV o Excel, o la exportación de productos de Shopify.">
        {user?.role === "admin" && <WipeDemoButton />}
      </PageHeader>

      <div className="ms-card p-5">
        <Stepper step={step} />

        {step === 1 && (
          <div className="space-y-5">
            {!imp ? (
              <div
                onClick={() => fileRef.current?.click()}
                className="rounded-2xl border-2 border-dashed border-border p-10 text-center cursor-pointer hover:border-primary transition-colors"
                data-testid="catalog-dropzone"
              >
                <UploadCloud className="h-10 w-10 mx-auto mb-3 text-primary" strokeWidth={1.5} />
                <p className="font-semibold">{uploading ? "Leyendo archivo…" : "Hacé clic para elegir tu archivo CSV o Excel"}</p>
                <p className="text-sm text-muted-foreground mt-1">Columnas flexibles: nombre, categoría, marca, precio, costo, stock, sku, descripción.</p>
                <p className="text-xs text-muted-foreground mt-1">La exportación de productos de Shopify (Productos → Exportar) se reconoce sola.</p>
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onFile} data-testid="catalog-file-input" />
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 text-sm">
                  <FileSpreadsheet className="h-5 w-5 text-primary" />
                  <span className="font-semibold">{imp.filename || "Archivo"}</span>
                  <Badge className="rounded-full border-0 bg-accent">{totalFilas} {esCsvWeb ? "variantes" : "filas"}</Badge>
                  <button onClick={reset} className="text-xs text-muted-foreground underline ml-2" data-testid="catalog-change-file">Cambiar archivo</button>
                </div>
                {esCsvWeb && (
                  <div className="rounded-2xl border border-border p-3" data-testid="catalog-shopify-info">
                    <p className="flex items-center gap-2 text-sm font-semibold"><Store className="h-4 w-4 text-primary" /> Archivo de Shopify reconocido</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Las filas ya están ordenadas: una por producto o variante, solo con los productos activos, y las columnas se eligieron solas.
                      La cantidad y el costo del archivo no se usan a menos que los elijas abajo.
                    </p>
                    {imp.avisos?.length > 0 && (
                      <details className="mt-2" data-testid="catalog-shopify-avisos">
                        <summary className="text-xs text-amber-600 cursor-pointer">
                          {imp.avisos_total ?? imp.avisos.length} para revisar: {Object.entries(resumenAvisos).map(([t, n]) => `${n} ${CSV_AVISO_LABELS[t] || t}`).join(" · ")}
                        </summary>
                        <ul className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                          {imp.avisos.map((a, i) => (
                            <li key={i} className="text-xs text-muted-foreground"><span className="font-semibold">{a.producto}</span> — {a.motivo}</li>
                          ))}
                        </ul>
                        {imp.avisos_total > imp.avisos.length && (
                          <p className="text-xs text-muted-foreground mt-1">Se muestran los primeros {imp.avisos.length}.</p>
                        )}
                      </details>
                    )}
                  </div>
                )}
                <div>
                  <h3 className="font-head font-semibold text-sm mb-2">Mapeá las columnas de tu archivo</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {(esCsvWeb ? [...CAMPOS, ...CAMPOS_WEB] : CAMPOS).map((c) => (
                      <div key={c.key}>
                        <Label>{c.label}{c.req && " *"}</Label>
                        <Select value={mapping[c.key] || "__none__"} onValueChange={(v) => setMapping({ ...mapping, [c.key]: v === "__none__" ? "" : v })}>
                          <SelectTrigger className="rounded-xl mt-1" data-testid={`map-select-${c.key}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">— no usar —</SelectItem>
                            {cabeceras.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {esCsvWeb && imp.mapping_opcional?.[c.key] && !mapping[c.key] && (
                          <button type="button" className="text-xs text-muted-foreground underline mt-1" data-testid={`map-usar-${c.key}`}
                            onClick={() => setMapping({ ...mapping, [c.key]: imp.mapping_opcional[c.key] })}>
                            Usar «{imp.mapping_opcional[c.key]}» del archivo
                          </button>
                        )}
                        {esCsvWeb && c.key === "stock_actual" && !mapping.stock_actual && (
                          <p className="text-[11px] text-muted-foreground mt-1">Sin elegir, el stock de los productos no cambia.</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="font-head font-semibold text-sm mb-2">Vista previa (primeras filas)</h3>
                  <div className="overflow-x-auto rounded-xl border border-border">
                    <table className="w-full text-xs">
                      <thead className="bg-muted"><tr>{cabeceras.map((h) => <th key={h} className="text-left p-2 whitespace-nowrap">{h}</th>)}</tr></thead>
                      <tbody>
                        {vistaPrevia.map((row, i) => (
                          <tr key={i} className="border-t border-border">
                            {cabeceras.map((h) => <td key={h} className="p-2 whitespace-nowrap">{row[h]}</td>)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button onClick={doMapear} className="rounded-full bg-primary hover:bg-primary/90 gap-2" data-testid="catalog-continue-step1">
                    Continuar <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {step === 2 && imp && (
          <div className="space-y-4">
            {esWeb && (
              <p className="text-xs text-muted-foreground flex items-center gap-2" data-testid="catalog-web-info">
                <Store className="h-4 w-4 text-primary shrink-0" /> Lista armada con los productos de la web. No cambia nada hasta que la confirmes y no se envía nada a Shopify.
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              {FILTROS.map((f) => (
                <button key={f.key} type="button" onClick={() => { setFiltro(f.key); setLimite(FILAS_POR_VEZ); }} data-testid={`import-filtro-${f.key}`}
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${filtro === f.key ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                  {(imp.rows_procesadas || []).filter(f.test).length} {f.texto}
                </button>
              ))}
            </div>
            <div className="space-y-2 max-h-[55vh] overflow-y-auto" data-testid="import-rows">
              {filas.slice(0, limite).map((row) => (
                <div key={row.fila_index} className="rounded-2xl border border-border p-3" data-testid={`import-row-${row.fila_index}`}>
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <Input value={row.nombre} onChange={(e) => updateRow(row, { nombre: e.target.value })} className="rounded-xl h-8 text-sm flex-1 min-w-[160px]" data-testid={`row-nombre-${row.fila_index}`} />
                    <NumeroInput valor={row.precio} step="any" onGuardar={(n) => updateRow(row, { precio: n })} className="rounded-xl h-8 text-sm w-24" placeholder="Precio" data-testid={`row-precio-${row.fila_index}`} />
                    <NumeroInput valor={row.costo} step="any" onGuardar={(n) => updateRow(row, { costo: n })} className="rounded-xl h-8 text-sm w-24" placeholder="Costo" data-testid={`row-costo-${row.fila_index}`} />
                    <NumeroInput valor={row.stock_actual} entero step="1" onGuardar={(n) => updateRow(row, { stock_actual: n })} className="rounded-xl h-8 text-sm w-28" placeholder="Sin cantidad"
                      title="Sin cantidad, el stock del producto no cambia" data-testid={`row-stock-${row.fila_index}`} />
                    <Select value={row.accion} onValueChange={(v) => updateRow(row, { accion: v })}>
                      <SelectTrigger className="rounded-xl h-8 text-sm w-40" data-testid={`row-accion-${row.fila_index}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="crear">Crear nuevo</SelectItem>
                        <SelectItem value="actualizar_existente" disabled={!row.product_id_match}>Actualizar existente</SelectItem>
                        <SelectItem value="omitir">Omitir</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {row.nombre_match && row.accion === "actualizar_existente" && (
                      <span className="text-xs text-blue-600">→ coincide con "{row.nombre_match}"{row.match_por ? ` (${CATALOG_MATCH_LABELS[row.match_por] || row.match_por})` : ""}</span>
                    )}
                    {row.accion === "actualizar_existente" && (row.cambios || []).filter((c) => c !== "nuevo").length > 0 && (
                      <span className="text-xs text-muted-foreground" data-testid={`row-cambios-${row.fila_index}`}>
                        Cambia: {row.cambios.filter((c) => c !== "nuevo").map((c) => CATALOG_CAMBIO_LABELS[c] || c).join(", ")}
                      </span>
                    )}
                    {row.accion === "crear" && row.sugerido_id && (
                      <button type="button" className="text-xs text-blue-600 underline" data-testid={`row-sugerido-${row.fila_index}`}
                        onClick={() => updateRow(row, { accion: "actualizar_existente", product_id_match: row.sugerido_id, nombre_match: row.sugerido_nombre, match_por: "elegido" })}>
                        ¿Es "{row.sugerido_nombre}"? Usar ese producto
                      </button>
                    )}
                    {row.issues.map((iss) => (
                      <Badge key={iss} className="rounded-full border-0 bg-amber-50 text-amber-600 gap-1">
                        <AlertTriangle className="h-3 w-3" /> {CATALOG_ISSUE_LABELS[iss] || iss}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
              {filas.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">No hay filas con ese filtro.</p>}
              {filas.length > limite && (
                <Button variant="outline" className="rounded-full w-full" onClick={() => setLimite(limite + FILAS_POR_VEZ)} data-testid="import-mostrar-mas">
                  Mostrar {Math.min(FILAS_POR_VEZ, filas.length - limite)} más ({filas.length - limite} sin mostrar)
                </Button>
              )}
            </div>
            <div className="flex justify-between">
              {sinPaso1 ? (
                <Button variant="outline" className="rounded-full gap-2" onClick={() => window.location.assign("/shopify")} data-testid="import-volver-shopify">
                  <ArrowLeft className="h-4 w-4" /> Volver a Shopify
                </Button>
              ) : (
                <Button variant="outline" className="rounded-full gap-2" onClick={() => setStep(1)}><ArrowLeft className="h-4 w-4" /> Atrás</Button>
              )}
              <Button onClick={() => setStep(3)} className="rounded-full bg-primary hover:bg-primary/90 gap-2" data-testid="catalog-continue-step2">
                Continuar <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {step === 3 && imp && (
          <div className="space-y-4 text-center py-4">
            {imp.status === "confirmada" ? (
              <>
                <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500" />
                <h3 className="font-head text-xl font-semibold">¡Importación confirmada!</h3>
                <div className="flex justify-center gap-3 flex-wrap">
                  <Badge className="rounded-full border-0 bg-emerald-50 text-emerald-600 text-sm px-3 py-1">{imp.resultado.creados} creados</Badge>
                  <Badge className="rounded-full border-0 bg-blue-50 text-blue-600 text-sm px-3 py-1">{imp.resultado.actualizados} actualizados</Badge>
                  <Badge className="rounded-full border-0 bg-gray-100 text-gray-500 text-sm px-3 py-1">{imp.resultado.omitidos} omitidos</Badge>
                </div>
                <div className="flex justify-center gap-3 flex-wrap">
                  {esWeb && <Button variant="outline" className="rounded-full" onClick={() => window.location.assign("/shopify")} data-testid="import-ir-shopify">Volver a Shopify</Button>}
                  <Button onClick={reset} className="rounded-full bg-primary hover:bg-primary/90" data-testid="catalog-import-another">Importar otro archivo</Button>
                </div>
              </>
            ) : (
              <>
                <h3 className="font-head text-xl font-semibold">Confirmar importación</h3>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  Se crearán {counts.crear || 0} productos nuevos, se actualizarán {counts.actualizar_existente || 0} existentes
                  y se omitirán {counts.omitir || 0} filas.{" "}
                  {esWeb
                    ? "El stock solo se mueve en las filas que traen una cantidad (queda como un ajuste con origen «Shopify»); las demás conservan el que tienen. No se envía nada a Shopify. Esta acción no se puede deshacer."
                    : "Esta acción genera movimientos de inventario y no se puede deshacer."}
                </p>
                <div className="flex justify-center gap-3">
                  <Button variant="outline" className="rounded-full" onClick={() => setStep(2)}>Revisar de nuevo</Button>
                  <Button disabled={confirming} onClick={doConfirmar} className="rounded-full bg-primary hover:bg-primary/90" data-testid="catalog-confirm-import">
                    {confirming ? "Confirmando…" : "Confirmar importación"}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {(!imp || step === 3) && <BulkPhotos />}
    </div>
  );
}
